import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Filter, Search, X, FlaskConical, Target, Users, Clock, Shield, Zap, Settings, RotateCcw, CircleDollarSign, Globe } from 'lucide-react';
import { defaultMatchingWeights, type MatchingWeights } from '../lib/matching';
import {
  CDMO_PARTNER_TYPE_SEARCH_FLEXIBLE_LABEL
} from '../constants';
import {
  countCapabilityOptions,
  filterCapabilityGroups,
  groupCapabilityOptions,
} from '../constants/detailedCapabilities';

const TIER2_CUSTOMIZABLE_WEIGHT_KEYS: Array<keyof MatchingWeights> = [
  'typical_batch_sizes',
  'engagement_scales',
  'speed_to_start',
  'budget',
  'operating_regions',
  'preferred_project_types',
];

const TIER2_SLIDER_LABELS: Record<keyof MatchingWeights, string> = {
  typical_batch_sizes: 'Batch Size',
  engagement_scales: 'Development Stage',
  speed_to_start: 'Project Kickoff',
  budget: 'Budget',
  operating_regions: 'Geography',
  preferred_project_types: 'Support Services',
  company_name: 'Company Search',
  primary_capabilities: 'Product Type Match',
  detailed_capabilities: 'Detailed Services',
  target_client_types: 'Partner Size',
  gmp_certifications: 'Certifications',
  description: 'Additional Details',
  facility_locations_details: 'Location Details',
};
import InfoTooltip from './common/InfoTooltip';
import { flexibleOptions, getPrimaryCapabilityLabel } from '../constants';
// User matching weights interface
export interface UserMatchingWeights {
  user_id: string;
  company_name?: number;
  description?: number;
  primary_capabilities?: number;
  gmp_certifications?: number;
  budget?: number;
  operating_regions?: number;
  preferred_project_types?: number;
  engagement_scales?: number;
  typical_batch_sizes?: number;
  target_client_types?: number;
  detailed_capabilities?: number;
  speed_to_start?: number;
  created_at?: string;
  updated_at?: string;
}

interface FilterSidebarProps {
  filters: {
    search: string;
    productType: string[];
    detailedServices: string[];
    developmentStage: string;
    supportType: string[];
    batchSize: string;
    budget: string;
    cdmoPartnerType: string;
    certifications: string[];
    location: string[];
    speedToStart: string;
  };
  onFilterChange: (filterType: string, value: string | string[]) => void;
  onClearFilters: () => void;
  availableOptions: {
    productTypeOptions: string[];
    developmentStageOptions: string[];
    supportTypeOptions: string[];
    batchSizeOptions: string[];
    budgetOptions: string[];
    cdmoPartnerTypeOptions: string[];
    certificationOptions: string[];
    locationOptions: string[];
    speedToStartOptions: string[];
    detailedCapabilitiesMap: Record<string, string[]>;
  };
  // Matching weights props
  matchingMode: 'system' | 'custom';
  customWeights: {
    company_name?: number;
    facility_locations_details?: number;
    primary_capabilities?: number;
    detailed_capabilities?: number;
    engagement_scales?: number;
    preferred_project_types?: number;
    typical_batch_sizes?: number;
    target_client_types?: number;
    gmp_certifications?: number;
    speed_to_start?: number;
    description?: number;
    budget?: number;
    operating_regions?: number;
  };
  onMatchingModeChange: (mode: 'system' | 'custom') => void;
  onWeightChange: (weightType: keyof MatchingWeights, value: number) => void;
  onSaveWeights: () => void;
  onResetWeights: () => void;
  isAuthenticated: boolean;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  availableOptions,
  matchingMode,
  customWeights,
  onMatchingModeChange,
  onWeightChange,
  onSaveWeights,
  onResetWeights,
  isAuthenticated
}) => {
  /** "Search capabilities" text for the detailed-services picker. Display-only — not a filter value. */
  const [detailedServiceSearch, setDetailedServiceSearch] = React.useState('');

  const handleCheckboxChange = (filterType: string, value: string) => {
    const currentValues = (filters[filterType as keyof typeof filters] as string[]) || [];
    let newValues: string[];

    if (flexibleOptions.includes(value)) {
      // If a "flexible" option is selected, it should be the only one
      newValues = currentValues.includes(value) ? [] : [value];
    } else {
      // If a specific option is selected, remove any "flexible" options
      newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues.filter(v => !flexibleOptions.includes(v)), value];
    }

    onFilterChange(filterType, newValues);
  };

  const handleInputChange = (filterType: string, value: string) => {
    onFilterChange(filterType, value);
  };

  /**
   * Capability options for the selected modalities, kept in modality → subcategory order.
   *
   * Deliberately not merged into one alphabetical list: the taxonomy uses modality-specific values
   * for shared concepts, so a flat sort would place "Lyophilization — Biologics" next to
   * "Lyophilization — Small Molecule" with nothing to tell them apart, and selecting two modalities
   * would produce a single 60-plus item scroll.
   *
   * Memoized because grouping allocates a copy of every group for every selected modality, and this
   * otherwise re-ran on each keystroke in the capability search — and on every unrelated filter
   * change, slider drag included.
   */
  const detailedServiceSections = useMemo(
    () =>
      filters.productType
        .map((productType) => ({
          productType,
          groups: filterCapabilityGroups(
            groupCapabilityOptions(productType, availableOptions.detailedCapabilitiesMap[productType] || []),
            detailedServiceSearch
          ),
        }))
        .filter((section) => section.groups.length > 0),
    [filters.productType, availableOptions.detailedCapabilitiesMap, detailedServiceSearch]
  );

  const totalDetailedServices = filters.productType.reduce(
    (total, productType) => total + (availableOptions.detailedCapabilitiesMap[productType]?.length ?? 0),
    0
  );
  const visibleDetailedServices = detailedServiceSections.reduce(
    (total, section) => total + countCapabilityOptions(section.groups),
    0
  );

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto overflow-x-hidden" >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Filter className="w-5 h-5 text-electric-blue mr-2" aria-hidden="true" />
          <h2 className="text-lg font-bold text-charcoal">Find Your Perfect Match</h2>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClearFilters}
          className="px-3 py-1 text-sm text-slate-gray hover:text-electric-blue transition-colors"
          aria-label="Clear filters"
        >
          <X className="w-4 h-4 inline mr-1" aria-hidden="true" />
          Clear
        </motion.button>
      </div>

      <div className="space-y-6">
        {/* Matching mode + Tier 2 weight sliders (top; gatekeeper fields have no sliders) */}
        {isAuthenticated && (
          <div className="border-b border-slate-gray/20 pb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-charcoal flex items-center">
                <Settings className="inline w-4 h-4 mr-1" />
                Matching Mode
              </h3>
            </div>

            <div className="space-y-3">
              <div className="flex space-x-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onMatchingModeChange('system')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    matchingMode === 'system'
                    ? 'bg-electric-blue text-white border border-electric-blue shadow-lg'
                    : 'bg-light-gray text-charcoal border border-slate-gray/40 hover:bg-electric-blue/10 hover:border-electric-blue/50'
                    }`}
                >
                  System Recommended
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onMatchingModeChange('custom')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    matchingMode === 'custom'
                    ? 'bg-electric-blue text-white border border-electric-blue shadow-lg'
                    : 'bg-light-gray text-charcoal border border-slate-gray/40 hover:bg-electric-blue/10 hover:border-electric-blue/50'
                    }`}
                >
                  Customize My Priorities
                </motion.button>
              </div>

              {matchingMode === 'custom' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 bg-electric-blue/5 rounded-lg p-3"
                >
                  <div className="text-xs text-slate-gray mb-2 flex items-center">
                    <div className="mr-1.5">
                      <InfoTooltip label="How priorities affect your matches">
                        <p>
                          Higher = more influence on Tier 2 ranking (batch size, stage, speed,
                          budget, geography, support) after gatekeeper pass.
                        </p>
                        <p>
                          Priorities re-order results only when two or more of these factors are set
                          as filters and the CDMOs differ on them. Results already at 100% match
                          cannot move, so the ranking stays the same.
                        </p>
                      </InfoTooltip>
                    </div>
                    <span>Adjust the importance of each factor in your CDMO matches:</span>
                  </div>

                  <div className="border-y border-slate-gray/15 divide-y divide-slate-gray/15">
                    {TIER2_CUSTOMIZABLE_WEIGHT_KEYS.map((key) => {
                      const sliderValue = customWeights[key] ?? defaultMatchingWeights[key] ?? 3;
                      const labelText = TIER2_SLIDER_LABELS[key];
                      const sliderId = `match-weight-${key}`;
                      return (
                      <div key={key} className="flex items-center justify-between gap-2 py-2.5">
                        <label
                          htmlFor={sliderId}
                          title={labelText}
                          className="text-xs font-medium text-charcoal min-w-0 flex-1 leading-snug break-words text-left"
                        >
                          {labelText}
                        </label>
                        <div className="flex shrink-0 items-center space-x-2">
                          <input
                            id={sliderId}
                            type="range"
                            min="0"
                            max="5"
                            step="1"
                            value={sliderValue}
                            onChange={(e) => onWeightChange(key, parseInt(e.target.value, 10))}
                            className="filter-match-weight-range w-16 rounded-lg bg-slate-gray/20"
                            style={{
                              background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${(sliderValue / 5) * 100}%, #E5E7EB ${(sliderValue / 5) * 100}%, #E5E7EB 100%)`
                            }}
                          />
                          <span className="text-xs font-mono text-charcoal w-4 text-center">
                            {sliderValue}
                          </span>
                        </div>
                      </div>
                    );
                    })}
                  </div>

                  <div className="flex space-x-2 pt-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={onSaveWeights}
                      className="flex-1 px-3 py-1.5 bg-lime-green text-white text-xs font-medium rounded-lg hover:shadow-md transition-all duration-300"
                    >
                      Save
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={onResetWeights}
                      className="px-3 py-1.5 bg-slate-gray/20 text-charcoal text-xs font-medium rounded-lg hover:bg-slate-gray/30 transition-all duration-300 flex items-center"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Reset
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-6 border-slate-gray/20">
        {/* Search companies */}
        <div>
          <label htmlFor="search" className="block text-sm font-medium text-charcoal mb-2">
            <Search className="inline w-4 h-4 mr-1" aria-hidden="true" />
            Search Companies
          </label>
          <input
            id="search"
            type="text"
            value={filters.search}
            onChange={(e) => handleInputChange('search', e.target.value)}
            placeholder="Search by company name..."
            className="w-full px-3 py-2 border border-slate-gray/30 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 text-sm text-charcoal bg-white"
          />
        </div>

        {/* 2. What are you developing? */}
        <div>
          <label htmlFor="productType" className="block text-sm font-medium text-charcoal mb-3">
            <FlaskConical className="inline w-4 h-4 mr-1" aria-hidden="true" />
            What are you developing?
          </label>
          <div className="space-y-2">
            {availableOptions.productTypeOptions.map((productType) => (
              <motion.label
                key={productType}
                whileHover={{ scale: 1.02 }}
                className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-electric-blue/5 transition-colors"
              >
                <input
                  type="checkbox"
                  id={`productType-${productType}`}
                  checked={filters.productType.includes(productType)}
                  onChange={() => handleCheckboxChange('productType', productType)}
                  className="w-4 h-4 text-electric-blue border-slate-gray/30 rounded focus:ring-electric-blue"
                />
                <span className="ml-2 text-sm text-charcoal">{getPrimaryCapabilityLabel(productType)}</span>
              </motion.label>
            ))}
          </div>
        </div>

        {/* 3. What specific services do you need? (Conditional) */}
        {filters.productType.length > 0 && totalDetailedServices > 0 && (
          <div>
            <label htmlFor="detailedServices" className="block text-sm font-medium text-charcoal mb-3">
              <Target className="inline w-4 h-4 mr-1" aria-hidden="true" />
              What CDMO capabilities or product expertise do you need? Select all that apply.
            </label>

            <div className="relative mb-2">
              <Search className="w-4 h-4 text-slate-gray absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
              <input
                id="detailedServices"
                type="text"
                value={detailedServiceSearch}
                onChange={(e) => setDetailedServiceSearch(e.target.value)}
                placeholder="Search capabilities..."
                aria-label="Search capabilities"
                className="w-full pl-9 pr-3 py-2 border border-slate-gray/30 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 text-sm text-charcoal bg-white"
              />
            </div>

            {detailedServiceSearch.trim() && (
              <p className="text-xs text-slate-gray mb-2">
                {visibleDetailedServices} of {totalDetailedServices} capabilities match "{detailedServiceSearch.trim()}"
              </p>
            )}

            {detailedServiceSections.length === 0 ? (
              <p className="text-sm text-slate-gray py-3">
                No capabilities match "{detailedServiceSearch.trim()}".
              </p>
            ) : (
              <div className="space-y-3 max-h-72 overflow-y-auto">
                {detailedServiceSections.map(({ productType, groups }) => (
                  <div key={productType}>
                    <h4 className="text-xs font-bold tracking-wide uppercase text-electric-blue mb-1.5">
                      {getPrimaryCapabilityLabel(productType)}
                    </h4>
                    {groups.map(({ group, options }) => (
                      <div key={`${productType}-${group || 'ungrouped'}`} className="mb-2 last:mb-0">
                        {group && (
                          <p className="text-[11px] font-semibold tracking-wide uppercase text-slate-gray mt-2 mb-1 pl-2">
                            {group}
                          </p>
                        )}
                        <div className="space-y-1">
                          {options.map((service) => (
                            <motion.label
                              key={service}
                              whileHover={{ scale: 1.02 }}
                              className="flex items-start cursor-pointer p-2 rounded-lg hover:bg-electric-blue/5 transition-colors"
                            >
                              <input
                                type="checkbox"
                                id={`detailedServices-${service}`}
                                checked={filters.detailedServices.includes(service)}
                                onChange={() => handleCheckboxChange('detailedServices', service)}
                                className="w-4 h-4 mt-0.5 shrink-0 text-electric-blue border-slate-gray/30 rounded focus:ring-electric-blue"
                              />
                              <span className="ml-2 text-sm text-charcoal">{service}</span>
                            </motion.label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* Certifications */}
        <div>
          <label htmlFor="certifications" className="block text-sm font-medium text-charcoal mb-3">
            <Shield className="inline w-4 h-4 mr-1" aria-hidden="true" />
            Which regulatory markets or compliance requirements does your CDMO need to support? Select all that apply.
          </label>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {availableOptions.certificationOptions.map((certification) => (
              <motion.label
                key={certification}
                whileHover={{ scale: 1.02 }}
                className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-electric-blue/5 transition-colors"
              >
                <input
                  type="checkbox"
                  id={`certification-${certification}`}
                  checked={filters.certifications.includes(certification)}
                  onChange={() => handleCheckboxChange('certifications', certification)}
                  className="w-4 h-4 text-electric-blue border-slate-gray/30 rounded focus:ring-electric-blue"
                />
                <span className="ml-2 text-sm text-charcoal">{certification}</span>
              </motion.label>
            ))}
          </div>
        </div>
        </div>

         {/* CDMO business profile (Tier 1 exact cdmo_business_profile — admin field on CDMO intake) */}
        <div>
          <label htmlFor="cdmoPartnerType" className="block text-sm font-medium text-charcoal mb-2">
            <Users className="inline w-4 h-4 mr-1" aria-hidden="true" />
            What type of CDMO partner are you looking for?
          </label>
          <select
            id="cdmoPartnerType"
            value={filters.cdmoPartnerType}
            onChange={(e) => handleInputChange('cdmoPartnerType', e.target.value)}
            aria-describedby="cdmoPartnerType-hint"
            className="w-full px-3 py-2 border border-slate-gray/30 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 text-sm text-charcoal bg-white"
          >
            {availableOptions.cdmoPartnerTypeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
            <option value="">{CDMO_PARTNER_TYPE_SEARCH_FLEXIBLE_LABEL}</option>
          </select>
        </div>

        <div className="space-y-6 border-slate-gray/20">
        {/* Development Stage */}
        <div>
          <label htmlFor="developmentStage" className="block text-sm font-medium text-charcoal mb-3">
            <Zap className="inline w-4 h-4 mr-1" aria-hidden="true" />
            What stage are you in?
          </label>
          <select
            id="developmentStage"
            value={filters.developmentStage}
            onChange={(e) => handleInputChange('developmentStage', e.target.value)}
            className="w-full px-3 py-2 border border-slate-gray/30 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 text-sm text-charcoal bg-white"
          >
            <option value="">Select development stage</option>
            {availableOptions.developmentStageOptions.map((stage) => (
              <option key={stage} value={stage}>{stage}</option>
            ))}
          </select>
        </div>

        {/* Support Services */}
        <div>
          <label htmlFor="supportType" className="block text-sm font-medium text-charcoal mb-3">
            <Target className="inline w-4 h-4 mr-1" aria-hidden="true" />
            Beyond core CDMO capabilities, what support would be helpful? Select all that apply.
          </label>
          <div className="space-y-2">
            {availableOptions.supportTypeOptions.map((supportType) => (
              <motion.label
                key={supportType}
                whileHover={{ scale: 1.02 }}
                className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-electric-blue/5 transition-colors"
              >
                <input
                  type="checkbox"
                  id={`supportType-${supportType}`}
                  checked={filters.supportType.includes(supportType)}
                  onChange={() => handleCheckboxChange('supportType', supportType)}
                  className="w-4 h-4 text-electric-blue border-slate-gray/30 rounded focus:ring-electric-blue"
                />
                <span className="ml-2 text-sm text-charcoal">{supportType}</span>
              </motion.label>
            ))}
          </div>
        </div>

        {/* Batch Size */}
        <div>
          <label htmlFor="batchSize" className="block text-sm font-medium text-charcoal mb-3">
            <Target className="inline w-4 h-4 mr-1" aria-hidden="true" />
            What estimated batch size or project scale do you expect to need?
          </label>
          <select
            id="batchSize"
            value={filters.batchSize}
            onChange={(e) => handleInputChange('batchSize', e.target.value)}
            className="w-full px-3 py-2 border border-slate-gray/30 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 text-sm text-charcoal bg-white"
          >
            <option value="">Select the closest range, if known.</option>
            {availableOptions.batchSizeOptions.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>

        {/* Project Kickoff */}
        <div>
          <label htmlFor="speedToStart" className="block text-sm font-medium text-charcoal mb-1">
            <Clock className="inline w-4 h-4 mr-1" aria-hidden="true" />
            When do you need to begin project kickoff with the selected CDMO?
          </label>
          <p id="speedToStart-hint" className="text-xs text-slate-gray mb-3">
            Select when the CDMO would need to be ready to formally begin the project after selection and required agreements.
          </p>
          <select
            id="speedToStart"
            value={filters.speedToStart}
            onChange={(e) => handleInputChange('speedToStart', e.target.value)}
            aria-describedby="speedToStart-hint"
            className="w-full px-3 py-2 border border-slate-gray/30 rounded-lg focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 text-sm text-charcoal bg-white"
          >
            <option value="">Select timeline</option>
            {availableOptions.speedToStartOptions.map((speed) => (
              <option key={speed} value={speed}>{speed}</option>
            ))}
          </select>
        </div>
        {/* Budget */}
        <div>
          <label className="block text-sm font-medium text-charcoal mb-3">
            <CircleDollarSign className="inline w-4 h-4 mr-1" aria-hidden="true" />
            Do you have an estimated project budget range?
          </label>
          <select
            value={filters.budget}
            onChange={(e) => handleInputChange('budget', e.target.value)}
            className="w-full px-3 py-2 border border-slate-gray/30 rounded-lg focus:ring-2 focus:ring-electric-blue text-sm bg-white"
          >
            <option value="">Select budget range, if known.</option>
            {availableOptions?.budgetOptions?.map((budgetOption) => (
              <option key={budgetOption} value={budgetOption}>{budgetOption}</option>
            ))}
          </select>
        </div>

        {/* Geography */}
        <div>
          <label className="block text-sm font-medium text-charcoal mb-3">
            <Globe className="inline w-4 h-4 mr-1" aria-hidden="true" />
            Preferred CDMO location(s), if any
          </label>
          <div className="space-y-2">
            {availableOptions?.locationOptions.map((region) => (
              <motion.label
                key={region}
                whileHover={{ scale: 1.02 }}
                className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-electric-blue/5 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={filters?.location?.includes(region) || false}
                  onChange={() => handleCheckboxChange('location', region)}
                  className="w-4 h-4 text-electric-blue border-slate-gray/30 rounded focus:ring-electric-blue"
                />
                <span className="ml-2 text-sm text-charcoal">{region}</span>
              </motion.label>
            ))}
          </div>
        </div>

        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;
